import React from 'react';
import {
  Badge,
  Box,
  Button,
  Container,
  Heading,
  Link as ChakraLink,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';

const proofs = [
  {
    eyebrow: 'Tenant controls',
    title: 'Isolation and access controls are visible in Business views',
    body:
      'Business workspaces expose tenant isolation, JWT-based access, audit logging, data residency, and API perimeter disclosures in the security posture surface.',
  },
  {
    eyebrow: 'DPA and roadmap',
    title: 'DPA template available, SOC 2 framed as roadmap',
    body:
      'DPA terms are available on request. SOC 2 Type I is presented as a roadmap workstream, not as an active certification or attestation.',
  },
  {
    eyebrow: 'Founder accountability',
    title: 'Direct owner for early advisory partners',
    body:
      'Alessandro Raiser, Madrid, remains the named commercial and product contact for firms evaluating Playground, Business, or White-label plus API paths.',
  },
];

const ProofYouCanTrust = () => {
  return (
    <Box as="section" bg="#060a14" py={{ base: 16, md: 24 }} borderTopWidth="1px" borderColor="brand.quantumBorder">
      <Container maxW="container.xl">
        <VStack spacing={{ base: 8, md: 12 }} align="stretch">
          <Box textAlign="center">
            <Badge
              bg="rgba(0, 212, 170, 0.1)"
              color="brand.quantumAccent"
              borderRadius="full"
              px={3}
              py={1}
              mb={5}
              letterSpacing="0.15em"
              textTransform="uppercase"
            >
              Trust posture
            </Badge>
            <Heading as="h2" size="2xl" color="white" mb={4}>
              Why advisory firms move forward with FinanSee
            </Heading>
            <Text color="brand.quantumMuted" fontSize="lg" maxW="3xl" mx="auto">
              The Playground tells the same story buyers will review during procurement:
              what is live today, what is on the roadmap, and who owns the rollout.
            </Text>
          </Box>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 5, md: 6 }}>
            {proofs.map((proof) => (
              <Box
                key={proof.title}
                bg="brand.quantumCard"
                borderRadius="2xl"
                p={6}
                boxShadow="0 18px 48px rgba(0, 0, 0, 0.28)"
                borderWidth="1px"
                borderColor="brand.quantumBorder"
              >
                <Text fontSize="xs" fontWeight="800" color="brand.quantumAccent" textTransform="uppercase" letterSpacing="0.12em" mb={3}>
                  {proof.eyebrow}
                </Text>
                <Heading size="md" color="white" mb={3}>
                  {proof.title}
                </Heading>
                <Text color="brand.quantumMuted">
                  {proof.body}
                </Text>
              </Box>
            ))}
          </SimpleGrid>

          <Box
            bg="rgba(255, 255, 255, 0.04)"
            borderWidth="1px"
            borderColor="brand.quantumBorder"
            borderRadius="2xl"
            p={{ base: 5, md: 7 }}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} alignItems="center">
              <Box>
                <Text color="brand.quantumAccent" fontSize="xs" fontWeight="800" letterSpacing="0.15em" textTransform="uppercase" mb={3}>
                  Next step
                </Text>
                <Heading color="white" size="lg" mb={3}>
                  Review plans, role coverage, and procurement disclosures.
                </Heading>
                <Text color="brand.quantumMuted">
                  Platform details, role-by-role access, and commercial paths are grouped
                  on the plans page so buyers can compare Solo, Team, Business and Enterprise.
                </Text>
              </Box>
              <VStack align={{ base: 'stretch', md: 'flex-end' }} spacing={3}>
                <Button
                  as={Link}
                  to="/platform"
                  size="lg"
                  bgGradient="linear(135deg, brand.quantumAccent, brand.quantumCyan)"
                  color="brand.quantumBg"
                  _hover={{ opacity: 0.9 }}
                >
                  Compare plans
                </Button>
                <ChakraLink
                  href="https://www.linkedin.com/in/alessandroraiser"
                  isExternal
                  color="brand.quantumAccent"
                  fontWeight="700"
                  _hover={{ color: 'brand.quantumCyan', textDecoration: 'none' }}
                >
                  Founder contact on LinkedIn
                </ChakraLink>
              </VStack>
            </SimpleGrid>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default ProofYouCanTrust;
